import { Controller } from "@hotwired/stimulus"

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

let scriptPromise = null

// Load api.js once per page lifetime. Explicit rendering is required: the
// implicit mode only scans the DOM when the script first loads, so a Turbo
// visit to /checkout after the script is cached would never render a widget.
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = SCRIPT_URL
    script.async = true
    script.onload = () => resolve(window.turnstile)
    script.onerror = () => {
      scriptPromise = null
      reject(new Error("Turnstile script failed to load"))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Cloudflare Turnstile widget for the bag checkout (card-testing defense).
 *
 * Renders into this element; Turnstile adds a hidden `cf-turnstile-response`
 * input inside it, so every path that posts the checkout form (card submit,
 * PayPal form.submit(), Apple Pay FormData) carries the token automatically.
 * The server redeems it in Bag::CheckoutController#create (see Turnstile).
 *
 * Managed mode + appearance "interaction-only": most shoppers never see the
 * widget; it only appears when Cloudflare wants a click.
 *
 * Exposed on the form as form._turnstile (mirrors form._nativeValidator) for
 * the validator and the wallet buttons:
 *   hasToken()      sync check, used before opening wallet sheets
 *   waitForToken()  resolves the token (or null after a timeout)
 *   reset()         fetch a fresh token after a failed JSON submit; tokens
 *                   are single-use
 */
export default class extends Controller {
  static values = { siteKey: String }
  static targets = ["widget", "error"]

  static WAIT_TIMEOUT_MS = 15000

  connect() {
    this.token = null
    this.widgetId = null
    this.waiters = []

    const form = this.element.closest("form")
    if (form) form._turnstile = this

    loadTurnstile()
      .then((turnstile) => this._render(turnstile))
      .catch((err) => {
        console.error("[turnstile]", err)
        this._showError("Security check could not load. Please disable any content blockers and refresh the page.")
      })
  }

  disconnect() {
    const form = this.element.closest("form")
    if (form && form._turnstile === this) delete form._turnstile

    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId)
    }
    this.widgetId = null
    this._resolveWaiters(null)
  }

  hasToken() {
    return Boolean(this.token)
  }

  waitForToken() {
    if (this.token) return Promise.resolve(this.token)

    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== waiter)
        resolve(null)
      }, this.constructor.WAIT_TIMEOUT_MS)
      const waiter = (token) => {
        clearTimeout(timer)
        resolve(token)
      }
      this.waiters.push(waiter)
    })
  }

  reset() {
    this.token = null
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.reset(this.widgetId)
    }
  }

  // Shown when a submit is attempted without a token. Method-agnostic copy:
  // this is also reached from the PayPal and Apple Pay paths, where there is
  // no Place Order button, and in interaction-only mode the widget is usually
  // invisible while it mints a replacement token.
  showPending() {
    // Keep a more specific error (load failure, widget error) if one is showing.
    if (this.hasErrorTarget && !this.errorTarget.hidden) return
    this._showError("Security check is still running. Please wait a moment and try again, or complete the check if one appears.")
  }

  _render(turnstile) {
    if (!this.element.isConnected || this.widgetId !== null) return

    // A Turbo restoration visit (Back button) replays the cached snapshot,
    // iframe and already-redeemed hidden token included. Start clean so the
    // form never carries a stale cf-turnstile-response alongside the new one.
    this.widgetTarget.replaceChildren()

    this.widgetId = turnstile.render(this.widgetTarget, {
      sitekey: this.siteKeyValue,
      action: "checkout",
      appearance: "interaction-only",
      "refresh-expired": "auto",
      callback: (token) => {
        this.token = token
        this._clearError()
        this._resolveWaiters(token)
      },
      "expired-callback": () => {
        this.token = null
      },
      "error-callback": () => {
        this.token = null
        this._showError("Security check failed. Please refresh the page and try again.")
        // No token is coming; release a pending Place Order instead of
        // leaving it on "Processing..." until the wait times out.
        this._resolveWaiters(null)
      },
    })
  }

  _resolveWaiters(token) {
    const waiters = this.waiters
    this.waiters = []
    waiters.forEach((w) => w(token))
  }

  _showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  _clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
